'use client';

import { useCallback, useEffect, useState } from 'react';

import type { ThreadSearchResult } from '@/lib/api';
import { searchDocs } from '@/lib/api/generated';

/** Coalesces keystrokes before asking Postgres to search post bodies. */
const DOC_SEARCH_DEBOUNCE_MILLISECONDS = 300;

/** Latest post-search response and its user-facing lifecycle state. */
interface DocSearchState {
  /** Query that produced this state, used to suppress results from previous input. */
  query: string;
  /** Conversations in server-provided relevance order with authorized post excerpts. */
  results: ThreadSearchResult[];
  /** Includes both the debounce period and the request in flight. */
  isLoading: boolean;
  /** Distinguishes a failed request from an empty successful result. */
  error: boolean;
}

/** Searches conversations while cancelling and ignoring responses for superseded input. */
export function useDocSearch(query: string): Omit<DocSearchState, 'query'> & { retry: () => void } {
  const normalizedQuery = query.trim();
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((previous) => previous + 1), []);
  const [state, setState] = useState<DocSearchState>({
    query: '',
    results: [],
    isLoading: false,
    error: false,
  });

  useEffect(() => {
    if (!normalizedQuery) {
      setState({ query: '', results: [], isLoading: false, error: false });
      return;
    }
    const controller = new AbortController();
    setState({ query: normalizedQuery, results: [], isLoading: true, error: false });
    const timeoutId = window.setTimeout(() => {
      const search = async () => {
        try {
          const results = await searchDocs(
            { query: normalizedQuery },
            { signal: controller.signal }
          );
          if (!controller.signal.aborted) {
            setState({ query: normalizedQuery, results, isLoading: false, error: false });
          }
        } catch (searchError) {
          if (!controller.signal.aborted) {
            console.error('Error searching posts:', searchError);
            setState({ query: normalizedQuery, results: [], isLoading: false, error: true });
          }
        }
      };
      void search();
    }, DOC_SEARCH_DEBOUNCE_MILLISECONDS);
    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [normalizedQuery, attempt]);

  if (!normalizedQuery || state.query !== normalizedQuery) {
    return { results: [], isLoading: Boolean(normalizedQuery), error: false, retry };
  }
  return { results: state.results, isLoading: state.isLoading, error: state.error, retry };
}
